import { twMerge } from "tailwind-merge"

export type LayoutSize = "site" | "content" | "wide-content"
export type LayoutAlign = "left" | "center" | "right"

interface BlockLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  /*
   * Seamless layout allows you using 100% width elements in mobile, but still obeys normal layout
   * restrictions in desktop/wide screens.
   * Align will have no effect when seamless is used.
   */
  seamless?: boolean
  /*
   * Content should have narrow layout. Still allows using seamless as well.
   */
  size?: LayoutSize
  /*
   * Alignment defaults to center, but can be useful when using BlockLayout within a constrained
   * container where content wants to align to eg. left while still allowing benefits from grid
   */
  align?: LayoutAlign
}

const getMaxWidthClass = (size: LayoutSize = "site") => {
  switch (size) {
    case "content":
      return "max-w-[60ch]"
    case "wide-content":
      return "max-w-[846px]"
    default:
      return "max-w-[1250px]"
  }
}

const getGutterClass = (seamless?: boolean) => {
  if (seamless) {
    return "px-0"
  }
  return "px-0"
}

const getAlignClass = (align: LayoutAlign = "center") => {
  switch (align) {
    case "left":
      return "justify-items-start"
    case "right":
      return "justify-items-end"
    default:
      return "justify-items-center"
  }
}

export const BlockLayout = ({
  className,
  seamless,
  size = "site",
  align = "center",
  children,
  ...props
}: BlockLayoutProps) => {
  const maxWidthClass = getMaxWidthClass(size)
  const gutterClass = getGutterClass(seamless)
  const alignClass = getAlignClass(align)

  return (
    <div
      className={twMerge("grid w-full mx-auto", maxWidthClass, gutterClass, alignClass, className)}
      {...props}
    >
      {children}
    </div>
  )
}

BlockLayout.displayName = "BlockLayout"

interface FullWidthLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  seamless?: boolean
  size?: LayoutSize
  align?: LayoutAlign
}

// Use FullWidthLayout to break out of parent layout's boundaries without having to resort to hacks like minus margin and transform
export const FullWidthLayout = ({
  children,
  seamless,
  size,
  className,
  align,
  ...props
}: FullWidthLayoutProps) => {
  if (size) {
    return (
      <div className="col-span-full" {...props}>
        <BlockLayout className={className} seamless={seamless} size={size} align={align}>
          {children}
        </BlockLayout>
      </div>
    )
  }

  return (
    <div className={twMerge("col-span-full", className)} {...props}>
      {children}
    </div>
  )
}
